import { CardMedia, Stack, Typography } from '@mui/material'
import React from 'react'

import { Caption } from '@f-fertighaus/applications/Next/components/common/ContentGallerySwiper/slideCard/Caption'
import { ImageWrapper } from '@f-fertighaus/applications/Next/components/common/ContentGallerySwiper/slideCard/ImageWrapper'
import { PriceInEuroLabel } from '@f-fertighaus/applications/Next/components/common/ContentGallerySwiper/slideCard/PriceInEuroLabel'
import { SquareMetersLabel } from '@f-fertighaus/applications/Next/components/common/ContentGallerySwiper/slideCard/SquareMetersLabel'
import { StyledCard } from '@f-fertighaus/applications/Next/components/common/ContentGallerySwiper/slideCard/StyledCard'
import { StyledCardContent } from '@f-fertighaus/applications/Next/components/common/ContentGallerySwiper/slideCard/StyledCardContent'
import { LinkWrapperWithWT } from '@f-fertighaus/applications/Next/components/common/LinkWrapper'
import { t } from '@f-fertighaus/applications/Next/public/locales/de'
import { SimilarHouse } from '@f-fertighaus/applications/Next/types/similarHousesResponse'
import { webtrack } from '@f-fertighaus/applications/Next/utils/webtrack'

interface HouseTileProps {
  item: SimilarHouse
}

export const SimilarHouseCard = React.memo(({ item }: HouseTileProps) => {
  const [displayedExteriorImage] = item?.exterior_images || [false]

  React.useEffect(() => {
    webtrack({
      category: 'item-collection',
      action: 'display',
      label: 'similar-house-card',
      location: 'similar-houses-section',
      values: [{ type: 'house_nid', value: item.nid }]
    })
  }, [item.nid])

  if (!displayedExteriorImage) {
    return null
  }

  return (
    <LinkWrapperWithWT
      url={item.url}
      wtPayload={{
        category: 'item',
        action: 'click',
        label: 'similar-house-card',
        location: 'similar-houses-section',
        values: [{ type: 'house_nid', value: item.nid }]
      }}>
      <StyledCard data-sel='crd-house-dflt'>
        <CardMedia
          component='img'
          alt={displayedExteriorImage.alt_text}
          src={displayedExteriorImage['fill-320x240']}
          style={{ width: '100%' }}
        />
        <StyledCardContent>
          <Stack justifyContent='space-between' sx={{ height: '100%' }}>
            <Stack
              direction='row'
              justifyContent='space-between'
              alignItems='flex-start'>
              <Stack>
                <Typography
                  gutterBottom
                  variant='body2'
                  fontWeight='fontWeightBold'
                  color={theme => theme.palette.text.primary}
                  sx={{ marginBottom: 0, lineHeight: 1 }}>
                  {item.name}
                </Typography>
                <Caption>{item.vendor_verbose.display_name}</Caption>
              </Stack>
              <ImageWrapper>
                <img
                  src={item.vendor_verbose.logo.medium}
                  alt='vendor-logo'
                  style={{ maxHeight: '32px' }}
                />
              </ImageWrapper>
            </Stack>
            <Stack>
              <Stack
                direction='row'
                justifyContent='space-between'
                alignItems='flex-start'>
                <PriceInEuroLabel>{item.price}</PriceInEuroLabel>
                <SquareMetersLabel>
                  {item.living_area_total && Math.round(item.living_area_total)}
                </SquareMetersLabel>
              </Stack>
              <Stack
                direction='row'
                justifyContent='space-between'
                alignItems='flex-start'>
                <Caption>{item.price_label}</Caption>
                <Caption>
                  {t(
                    'houseDetailPage.sections.details.overview.labels.living_area_total'
                  )}
                </Caption>
              </Stack>
            </Stack>
          </Stack>
        </StyledCardContent>
      </StyledCard>
    </LinkWrapperWithWT>
  )
})

SimilarHouseCard.displayName = 'SimilarHouseCard'
